import { z } from "zod";

/**
 * Shape of the Results filter state as it crosses the wire for Tab 4's PDF
 * export: the client POSTs it to /api/report/pdf, the server forwards it to
 * the headless print route as a `?f=` param, and that route parses it again.
 * Mirrors client/src/lib/results-filters.ts's ResultsFilterState — the
 * assignability of the two is asserted in __tests__/report-filters.test.ts.
 *
 * Bounded on purpose: this is untrusted input that ends up in a URL.
 */
const shortString = z.string().max(200);
const importance1to4 = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
const importance0to4 = z.union([z.literal(0), importance1to4]);

export const reportFiltersSchema = z.object({
  hazards: z
    .array(z.enum(["heat_stress", "changing_precipitation", "water_stress", "drought", "floods", "landslide"]))
    .max(6),
  countries: z.array(shortString).max(100),
  cities: z.array(shortString).max(500),
  facilities: z.array(shortString).max(1000),
  assetImportance: z.array(importance1to4).max(4),
  valueChainStageIds: z.array(shortString).max(100),
  valueChainImportance: z.array(importance0to4).max(5),
  scenario: z.enum(["SSP2-4.5", "SSP5-8.5"]),
  // At least one horizon: with none there is nothing to render.
  horizons: z.array(z.enum(["historical", "2030", "2050"])).min(1).max(3),
});

export type ReportFilters = z.infer<typeof reportFiltersSchema>;

/** URL-safe value for the print route's `?f=` query param. */
export function encodeFiltersParam(filters: ReportFilters): string {
  return encodeURIComponent(JSON.stringify(filters));
}

/** Takes the already-decoded `searchParams.get("f")`. null on missing/garbage/invalid input. */
export function decodeFiltersParam(raw: string | null): ReportFilters | null {
  if (!raw) return null;
  try {
    const parsed = reportFiltersSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
